import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { convertBpToDecimal, formatNumberLocale, sortByShiftType } from "@/utils/helpers";
import { CompactFieldRow } from "./CompactFieldRow";
import ShiftPricingCard from "./ShiftPricingCard";
import Decimal from "decimal.js";
import { PermanentService, Service, ServiceType, ShiftType } from "@/graphql/__generated__/graphql";

interface ServiceCompactPricingCardProps {
  service: Service;
  showName?: boolean;
  useCard?: boolean;
}

/**
 * ServiceCompactPricingCard component renders service pricing details.
 * Includes total section, base section, and shift breakdowns.
 * Uses looped field rendering for maintainability.
 *
 * @component
 * @example
 * <ServiceCompactPricingCard service={serviceData} showName={true} useCard={true} />
 */
function ServiceCompactPricingCard({
  service,
  showName = true,
  useCard = true,
}: ServiceCompactPricingCardProps) {
  const { t } = useTranslation(["components", "common"]);
  const intl = useIntl();

  const pricing = service.pricing;
  const cumulatedBasePricing = pricing.servicePricing.find((p) => p.type === ShiftType.Cumulated);

  // Get breakdown values for total section
  const baseRevenue = cumulatedBasePricing?.revenue ?? 0;
  const basePricePerHour = cumulatedBasePricing?.pricePerHour ?? 0;

  // Total section fields
  const totalFields = [
    {
      key: "surcharge",
      label: t("common:pricing.labels.surcharge"),
      value: `${formatNumberLocale(intl, convertBpToDecimal(pricing.totalSurcharge), "decimal", false, false, 0, 2)}%`,
      rawCopyValue: convertBpToDecimal(pricing.totalSurcharge),
      formatType: "none" as const,
    },
    {
      key: "totalRevenue",
      label: t("common:pricing.labels.totalrevenue"),
      value: pricing.totalRevenue,
      formatType: "currency" as const,
    },
    {
      key: "totalPricePerHour",
      label: t("common:pricing.labels.totalpriceperhour"),
      value: pricing.totalPricePerHour,
      formatType: "currency" as const,
    },
    {
      key: "totalYearlyRate",
      label: t("common:pricing.labels.totalyearlyrate"),
      value: pricing.yearlyRate,
      formatType: "currency" as const,
      isUnavailable: pricing.hasCustomSchedule,
      tooltip: pricing.hasCustomSchedule ? t("pricedetails.tooltips.yearlyrateunavailable") : undefined,
    },
    {
      key: "totalMonthlyRate",
      label: t("common:pricing.labels.totalmonthlyrate"),
      value: pricing.monthlyRate,
      formatType: "currency" as const,
      isUnavailable: pricing.hasCustomSchedule,
      tooltip: pricing.hasCustomSchedule ? t("pricedetails.tooltips.monthlyrateunavailable") : undefined,
    },
    ...(service.type === ServiceType.Mobile
      ? [
        {
          key: "pricePerMinute",
          label: t("pricedetails.labels.priceperminute"),
          value: new Decimal(pricing.totalPricePerHour).dividedBy(60).toNumber(),
          formatType: "currency" as const,
        },
      ]
      : []),
  ];

  // Base section fields
  const baseFields = [
    {
      key: "employeeCount",
      label: t("common:pricing.labels.employeecount"),
      value: pricing.totalEmployeeCount,
      formatType: "decimal" as const,
    },
    {
      key: "baseRevenue",
      label: t("pricedetails.labels.baserevenue"),
      value: baseRevenue,
      formatType: "currency" as const,
    },
    {
      key: "basePricePerHour",
      label: t("pricedetails.labels.basepriceperhour"),
      value: basePricePerHour,
      formatType: "currency" as const,
    },
    {
      key: "baseMargin",
      label: t("pricedetails.labels.basemargin"),
      value: `${formatNumberLocale(intl, convertBpToDecimal(service.margin), "decimal", false, false, 0, 2)}%`,
      rawCopyValue: convertBpToDecimal(service.margin),
      formatType: "none" as const,
    },
    {
      key: "monthlyRate",
      label: t("common:pricing.labels.monthlyrate"),
      value: pricing.monthlyRate,
      formatType: "currency" as const,
      isUnavailable: pricing.hasCustomSchedule,
      tooltip: pricing.hasCustomSchedule ? t("pricedetails.tooltips.monthlyrateunavailable") : undefined,
    },
    {
      key: "yearlyRate",
      label: t("common:pricing.labels.yearlyrate"),
      value: pricing.yearlyRate,
      formatType: "currency" as const,
      isUnavailable: pricing.hasCustomSchedule,
      tooltip: pricing.hasCustomSchedule ? t("pricedetails.tooltips.yearlyrateunavailable") : undefined,
    },
    {
      key: "hours",
      label: t("common:pricing.labels.hours"),
      value: new Decimal(service.minutesTotal ?? 0).dividedBy(60).toNumber(),
      formatType: "decimal" as const,
    },
    ...(service.hoursBriefingPerEmployee !== null && service.hoursBriefingPerEmployee !== undefined
      ? [
        {
          key: "hoursBriefing",
          label: t("common:pricing.labels.hoursbriefing"),
          value: `${service.hoursBriefingPerEmployee}h`,
          formatType: "none" as const,
        },
      ]
      : []),
    ...(service.type === ServiceType.Permanent && (service as PermanentService).transferredEmployeePercentage !== null
      ? [
        {
          key: "transferredEmployeePercentage",
          label: t("common:pricing.labels.transferredemployeepercentage"),
          value: `${convertBpToDecimal((service as PermanentService).transferredEmployeePercentage ?? 0)}%`,
          rawCopyValue: convertBpToDecimal((service as PermanentService).transferredEmployeePercentage ?? 0),
          formatType: "none" as const,
        },
      ]
      : []),
    ...(service.type === ServiceType.Permanent && (service as PermanentService).pauseIncluded !== null
      ? [
        {
          key: "pauseIncluded",
          label: t("common:pricing.labels.pauseincluded"),
          value: (service as PermanentService).pauseIncluded ? t("common:yes") : t("common:no"),
          formatType: "none" as const,
        },
      ]
      : []),
  ];

  // Get minutes for shift types
  const getMinutesForShiftType = (shiftType: string): number => {
    const minutesMap: Record<string, number | undefined> = {
      cumulated: service.minutesTotal,
      day: service.minutesDay ?? undefined,
      night: service.minutesNight ?? undefined,
      sunday: service.minutesSunday ?? undefined,
      sunday_night: service.minutesSundayNight ?? undefined,
      holiday: service.minutesHoliday ?? undefined,
      holiday_night: service.minutesHolidayNight ?? undefined,
    };
    return minutesMap[shiftType.toLowerCase()] ?? 0;
  };

  const content = (
    <div className="space-y-2">
      {showName && (
        <>
          <div className="font-semibold text-sm mb-2">{service.name}</div>
          <Separator />
        </>
      )}

      {/* Total Section */}
      <div className="space-y-1">
        <div className="text-xs font-semibold text-muted-foreground">{t("pricedetails.sections.total")}</div>
        <div className="text-xs text-muted-foreground italic">{t("pricedetails.sections.totalbreakdownhint")}</div>

        {totalFields.map((field) => (
          <CompactFieldRow
            key={field.key}
            label={field.label}
            value={field.value}
            intl={intl}
            formatType={field.formatType}
            isUnavailable={field.isUnavailable}
            tooltip={field.tooltip}
            rawCopyValue={field.rawCopyValue}
          />
        ))}
      </div>

      <Separator />

      {/* Base Section */}
      <div className="space-y-1">
        <div className="text-xs font-semibold text-muted-foreground">{t("pricedetails.sections.base")}</div>
        {baseFields.map((field) => (
          <CompactFieldRow
            key={field.key}
            label={field.label}
            value={field.value}
            intl={intl}
            formatType={field.formatType}
            isUnavailable={field.isUnavailable}
            tooltip={field.tooltip}
            rawCopyValue={field.rawCopyValue}
          />
        ))}
      </div>

      {/* Pricing Details Breakdown */}
      {pricing.servicePricing.length > 0 && (
        <>
          <Separator className="my-2" />
          <div className="space-y-2">
            <div className="text-xs font-semibold text-muted-foreground">
              {t("pricedetails.pricingdetails")}
            </div>
            {sortByShiftType(pricing.servicePricing).map((detail) => {
              const additionalPricing = pricing.carPurchasePricing.find((cp) => cp.type === detail.type);
              const shiftMinutes = getMinutesForShiftType(detail.type);

              return (
                <ShiftPricingCard
                  key={`pricing-detail-${detail.type}`}
                  detail={detail}
                  additionalPricing={additionalPricing}
                  shiftMinutes={shiftMinutes}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  return useCard ? <Card className="p-3 space-y-2">{content}</Card> : content;
}

export default ServiceCompactPricingCard;
